import { mediaQueryLarge, isMobileBreakpoint } from "@theme/utilities";

// Accordion
class AccordionCustom extends HTMLElement {
  #isAnimating = false;

  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector("details");

    if (!(details instanceof HTMLDetailsElement))
      throw new Error("Details element not found");

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector("summary");

    if (!(summary instanceof HTMLElement))
      throw new Error("Summary element not found");

    return summary;
  }

  /** @type {HTMLElement} */
  get content() {
    return (
      this.querySelector(".details-content") ||
      this.details.querySelector("summary + *")
    );
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === "true";
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === "true";
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === "true";
  }

  #controller = new AbortController();

  connectedCallback() {
    // Some sections (featured collection, fabric details) use <accordion-custom> only as a
    // styling hook and open/close it with their own script. They have no <details>, so skip
    // the setup here instead of throwing "Details element not found".
    if (!(this.querySelector("details") instanceof HTMLDetailsElement)) return;

    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    // Sync [open] attribute with details.open state on initialization
    if (this.#hasCssAnimation) {
      this.#syncOpenAttribute();
    }

    this.addEventListener("keydown", this.#handleKeyDown, { signal });
    this.summary.addEventListener("click", this.handleClick, { signal });
    mediaQueryLarge.addEventListener("change", this.#handleMediaQueryChange, {
      signal,
    });

    // Theme editor integration - open accordion when block is selected in customizer
    document.addEventListener("shopify:block:select", this.#handleBlockSelect, {
      signal,
    });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Checks if the accordion has CSS-based animation structure (grid-template-rows approach).
   * @returns {boolean}
   */
  get #hasCssAnimation() {
    return !!this.querySelector(".accordion-content-wrapper");
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    if (this.#isAnimating) {
      event.preventDefault();
      return;
    }

    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if (
      (isMobile && this.#disableOnMobile) ||
      (isDesktop && this.#disableOnDesktop)
    ) {
      event.preventDefault();
      return;
    }

    // If CSS animation structure is present (accordion-content-wrapper), ALWAYS use CSS animation
    // Must prevent default to stop native details toggle from interfering
    if (this.#hasCssAnimation) {
      event.preventDefault();
      this.#toggleWithCssAnimation();
      return;
    }

    // If browser supports modern CSS animation for details, let it handle it
    if (window.CSS && CSS.supports("interpolate-size", "allow-keywords")) {
      return;
    }

    event.preventDefault();
    this.toggle();
  };

  /**
   * Toggles accordion using CSS animation (for accordion-content-wrapper structure).
   * Sets open attribute on accordion-custom element for CSS targeting.
   */
  #toggleWithCssAnimation() {
    const isCurrentlyOpen = this.hasAttribute("open") || this.details.open;

    if (isCurrentlyOpen) {
      // Closing: remove [open] attr first so CSS can animate to 0fr
      this.removeAttribute("open");

      // Wait for CSS animation to complete, then close details
      setTimeout(() => {
        this.details.open = false;
      }, 500); // Match CSS transition duration
    } else {
      // Opening: open details first so content is in DOM, then add [open] for CSS animation
      this.details.open = true;

      // Use requestAnimationFrame to ensure details is open before CSS animation starts
      requestAnimationFrame(() => {
        this.setAttribute("open", "");
      });
    }
  }

  /**
   * Toggles the accordion state with animation.
   */
  toggle() {
    if (this.details.open) {
      this.close();
    } else {
      this.open();
    }
  }

  /**
   * Opens the accordion with animation.
   */
  open() {
    if (this.#isAnimating || this.details.open) return;

    this.#isAnimating = true;
    this.details.open = true;

    const content = this.content;
    if (!content) {
      this.#isAnimating = false;
      return;
    }

    const startHeight = 0;
    const endHeight = content.scrollHeight;

    content.style.overflow = "hidden";

    const animation = content.animate(
      [
        { height: `${startHeight}px`, opacity: 0 },
        { height: `${endHeight}px`, opacity: 1 },
      ],
      {
        duration: 400,
        easing: "cubic-bezier(0.4, 0, 0.2, 1)",
      },
    );

    animation.onfinish = () => {
      this.#isAnimating = false;
      content.style.overflow = "";
      content.style.height = "";
    };
  }

  /**
   * Closes the accordion with animation.
   */
  close() {
    if (this.#isAnimating || !this.details.open) return;

    this.#isAnimating = true;
    const content = this.content;
    if (!content) {
      this.details.open = false;
      this.#isAnimating = false;
      return;
    }

    const startHeight = content.offsetHeight;
    const endHeight = 0;

    content.style.overflow = "hidden";

    const animation = content.animate(
      [
        { height: `${startHeight}px`, opacity: 1 },
        { height: `${endHeight}px`, opacity: 0 },
      ],
      {
        duration: 400,
        easing: "cubic-bezier(0.4, 0, 0.2, 1)",
      },
    );

    animation.onfinish = () => {
      this.details.open = false;
      this.#isAnimating = false;
      content.style.overflow = "";
      content.style.height = "";
    };
  }

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();

    // Sync [open] attribute after media query change
    if (this.#hasCssAnimation) {
      this.#syncOpenAttribute();
    }
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile && this.hasAttribute("open-by-default-on-mobile")) ||
      (!isMobile && this.hasAttribute("open-by-default-on-desktop"));
  }

  /**
   * Syncs the [open] attribute on accordion-custom with the details.open state.
   * Used for CSS animation targeting.
   */
  #syncOpenAttribute() {
    if (this.details.open) {
      this.setAttribute("open", "");
    } else {
      this.removeAttribute("open");
    }
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === "Escape" && this.#closeWithEscape) {
      event.preventDefault();

      this.close();
      this.summary.focus();
    }
  }

  /**
   * Handles the shopify:block:select event in theme editor
   * Opens the accordion when its block is selected for easier editing
   *
   * @param {CustomEvent} event - The block select event.
   */
  #handleBlockSelect = (event) => {
    // Check if the selected block is within this accordion
    if (event.target && event.target instanceof HTMLElement) {
      const accordion = event.target.closest("accordion-custom");

      // If this accordion contains the selected block, open it
      if (accordion === this) {
        this.open();
      }
    }
  };
}

if (!customElements.get("accordion-custom")) {
  customElements.define("accordion-custom", AccordionCustom);
}
